<script setup lang="ts">
import { ApiGetCrystalOrder } from "@/apis/crystal";
import { PaymentStatus } from "@/enums/payment";
import { useGlobalUser } from "@/stores/globalUser";
import { CrystalOrder } from "@/types/entity";
import { useRouteQuery } from "@vueuse/router";
import { showDialog, showFailToast } from "vant";
import { onBeforeUnmount, watch } from "vue";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

const { t } = useI18n();
const user = useGlobalUser();
const router = useRouter();
const route = useRoute();

const show = ref(false);
const packageId = useRouteQuery<number>("packageId", () => 0, {
  transform: (val) => Number(val),
});
const method = useRouteQuery<string>("method", "");
const uuid = useRouteQuery<string>("uuid", "");
const timer = ref<NodeJS.Timeout | null>(null);
const order = ref<CrystalOrder | null>(null);

const showPaymentSuccessDialog = (quantity: number) => {
  showDialog({
    title: t("Message.paymentSuccessful"),
    message: t("Message.crystalPaymentSuccess", {
      count: quantity,
    }),
    showCancelButton: false,
  });
};
/**
 * 通过轮询检查订单状态
 */
const checkOrderStatus = async () => {
  const res = await ApiGetCrystalOrder(uuid.value);
  order.value = res.item;
  if (order.value.paymentStatus !== PaymentStatus.Pending) {
    if (timer.value) {
      clearInterval(timer.value);
    }
    await user.refreshUser();
    if (order.value.paymentStatus === PaymentStatus.Paid) {
      showPaymentSuccessDialog(order.value.quantity);
    } else {
      showFailToast({
        message: t("Message.paymentCancelled"),
        duration: 1000,
      });
    }
    router.replace({
      name: "Crystal",
    });
  }
};

watch([packageId, method, uuid], async () => {
  if (route.name !== "Crystal") return;
  if (packageId.value && method.value && uuid.value) {
    show.value = true;
    await checkOrderStatus();
    // 设置3秒轮询
    timer.value = setInterval(checkOrderStatus, 3000);
  } else {
    // 如果订单不存在，则关闭弹窗
    if (timer.value) {
      clearInterval(timer.value);
    }
    show.value = false;
  }
});

onBeforeUnmount(() => {
  if (timer.value) {
    clearInterval(timer.value);
  }
});

/**
 * 通过点击按钮设置订单状态
 */
const onPaymentResultButtonClick = async (
  status: PaymentStatus.Paid | PaymentStatus.Canceled
) => {
  if (status === PaymentStatus.Paid) {
    const res = await ApiGetCrystalOrder(uuid.value);
    order.value = res.item;
    if (order.value.paymentStatus === PaymentStatus.Paid) {
      await user.refreshUser();
      showPaymentSuccessDialog(order.value.quantity);
      show.value = false;
      router.replace({
        name: "Crystal",
      });
    } else {
      showFailToast({
        message: "尚未完成支付",
        duration: 1000,
      });
      return;
    }
  } else {
    showFailToast({
      message: t("Message.paymentCancelled"),
      duration: 1000,
    });
    show.value = false;
    router.replace({
      name: "Crystal",
    });
  }
};
</script>
<template>
  <VanPopup v-model:show="show" class="rounded">
    <div
      class="bg-white text-black font-sans flex flex-col items-center justify-center min-w-64"
    >
      <div class="p-4 text-center">{{ t("Message.confirmPayment") }}</div>
      <div
        class="p-4 text-center text-rose-500 border-black/10 border-t w-full"
        @click="onPaymentResultButtonClick(PaymentStatus.Paid)"
      >
        {{ t("Buttons.paymentCompleted") }}
      </div>
      <div
        class="p-4 text-center border-black/10 border-t w-full text-black/50"
        @click="onPaymentResultButtonClick(PaymentStatus.Canceled)"
      >
        {{ t("Buttons.paymentCancelled") }}
      </div>
    </div>
  </VanPopup>
</template>
